/* eslint-disable react/no-unescaped-entities */

import { Loading } from 'components/Loading'
import { WithAccountProps } from 'hoc/withAccount'
import useAppointments from 'hooks/useAppointments/useAppointments'
import { memo } from 'react'
import StaticTimeline, { TimelineItem } from './StaticTimeline'
import './styles.scss'
import { useFlags } from 'launchdarkly-react-client-sdk'
import useOnboardingData from 'hooks/useUser/useOnboardingData'

const TIMELINE_ITEMS: Record<string, TimelineItem[]> = {
  insurance: [
    {
      title: 'YOUR FIRST VISITS',
      description:
        'Meet with your medical and nutrition partners to review symptoms, get early guidance, and map your care plan.',
    },
    {
      title: 'COMPLETE YOUR LABS',
      description:
        'Get in-depth bloodwork and/or imaging to check hormones, inflammation, and more.',
    },
    {
      title: 'BUILD YOUR PLAN',
      description:
        'Tailor your care – prescriptions, supplements, diet, and movement – to your needs.',
    },
    {
      title: 'REVISIT, REFINE, REPEAT',
      description:
        'Meet regularly with your team to refine your care plan and stay aligned with your goals.',
    },
  ],
  'full-program': [
    {
      title: 'First Medical Visit',
      description:
        'Discuss your symptoms, health history, and goals. Get early guidance and a roadmap for your care.',
    },
    {
      title: 'Complete Your Labs',
      description:
        'Get in-depth bloodwork and/or imaging to check hormones, inflammation, and more.',
    },
    {
      title: 'Build Your Plan',
      description:
        'Tailor your care – prescriptions, supplements, diet, and movement – to your needs.',
    },
    {
      title: 'Meet Your Dietitian',
      description:
        'Discuss nutrition and lifestyle strategies that align with your plan and support your progress.',
    },
    {
      title: 'Revisit, Refine, Repeat',
      description:
        'Meet regularly with your care team to refine your care plan and stay aligned with your goals.',
    },
  ],
}

const Timeline: React.FC<WithAccountProps> = ({ user }) => {
  const productType = user?.stripeCustomer?.product?.type
  const timelineItemsKey =
    productType && productType in TIMELINE_ITEMS ? productType : 'insurance'
  const timelineItems = TIMELINE_ITEMS[timelineItemsKey]

  const { hasBookedNumberMDAppointments, isLoading } = useAppointments()
  const { weightManagementPatientJourneyEnabled } = useFlags()
  const { onboardingData } = useOnboardingData()

  if (isLoading) {
    return <Loading />
  }

  if (
    weightManagementPatientJourneyEnabled &&
    onboardingData?.what_brings_you_to_allara === 'weight_loss'
  ) {
    return null
  }

  // We don't want to show a timeline for nutrition-only users
  if (user?.stripeCustomer?.product?.type === 'nutrition-only') {
    return null
  }

  if (
    (user?.stripeCustomer?.product?.type === 'full-program' ||
      user?.stripeCustomer?.product?.type === 'insurance') &&
    hasBookedNumberMDAppointments(4)
  ) {
    return null
  }

  return <StaticTimeline timelineItems={timelineItems} />
}

export default memo(Timeline)
